import { X, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PreviewTable from "./PreviewTable";
import { API, MappingEditor, PhaseLockControl, Stat, Section, FileUpload } from "../App";

function Phase5({ session, refresh }) {
  const [busy, setBusy] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingXlsx, setGeneratingXlsx] = useState(false);
  const result = session.phase_results?.phase5;
  const phase5Ok = !!(result && result.ok);
  const locked = !!(session.locked_phases || {}).phase5;
  const verified = !!session.verified;

  const [manualAdjustments, setManualAdjustments] = useState(() => {
    if (session.manual_adjustments) return session.manual_adjustments;
    if (session.misc_revenue) return [{ amount: session.misc_revenue, comments: session.misc_revenue_comments || "" }];
    return [];
  });

  useEffect(() => {
    if (session.manual_adjustments) {
      setManualAdjustments(session.manual_adjustments);
    } else if (session.misc_revenue) {
      setManualAdjustments([{ amount: session.misc_revenue, comments: session.misc_revenue_comments || "" }]);
    } else {
      setManualAdjustments([]);
    }
  }, [session.manual_adjustments, session.misc_revenue, session.misc_revenue_comments]);

  const handleMiscSave = async (newAdjustments = manualAdjustments) => {
    try {
      await axios.patch(`${API}/audit/session/${session.id}`, {
        manual_adjustments: newAdjustments
      });
      toast.success("Saved manual adjustments");
      refresh();
    } catch (e) {
      toast.error(`Failed to save adjustments: ${e.response?.data?.detail || e.message}`);
    }
  };

  const updateAdjustment = (index, field, value) => {
    const newAdj = [...manualAdjustments];
    newAdj[index][field] = value;
    setManualAdjustments(newAdj);
  };
  
  const removeAdjustment = (index) => {
    const newAdj = manualAdjustments.filter((_, i) => i !== index);
    setManualAdjustments(newAdj);
    handleMiscSave(newAdj);
  };

  const addAdjustment = () => {
    if (manualAdjustments.length >= 3) return;
    setManualAdjustments([...manualAdjustments, { amount: 0, comments: "" }]);
  };

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/audit/session/${session.id}/phase5`);
      if (res.data && res.data.ok === false) throw new Error(res.data.error || "Phase 5 backend error");
      toast.success("Phase 5 complete");
      refresh();
    } catch (e) {
      toast.error(`Phase 5 failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleVerified = async (next) => {
    try {
      await axios.patch(`${API}/audit/session/${session.id}`, { verified: !!next });
      toast.success(next ? "Marked as verified" : "Verification removed");
      refresh();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Generic blob-downloader used by both PDF and Excel exports. Fetches
  // the binary via axios as a blob and triggers a programmatic <a download>
  // click — works reliably inside iframe previews where window.open can
  // leave a blank tab and silently swallow Content-Disposition errors.
  const downloadAuditFile = async ({ path, mime, defaultName, setBusyFlag, label }) => {
    const url = `${API}${path}`;
    const tid = toast.loading(`Generating ${label}…`);
    setBusyFlag(true);
    try {
      const res = await axios.get(url, { responseType: "blob", timeout: 180000 });
      let filename = defaultName;
      const cd =
        res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      if (cd) {
        const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
        if (m) filename = m[1];
      }
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
      toast.success(`Downloaded ${filename}`, { id: tid });
    } catch (e) {
      // axios with responseType:"blob" returns the error body as a Blob too,
      // so try to read it as text to surface the backend message.
      let detail = e?.message || "Unknown error";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          try {
            detail = JSON.parse(text)?.detail || text || detail;
          } catch {
            detail = text || detail;
          }
        } catch {
          /* ignore */
        }
      } else if (data?.detail) {
        detail = data.detail;
      }
      toast.error(`${label} failed: ${detail}`, { id: tid });
    } finally {
      setBusyFlag(false);
    }
  };

  const generateReport = () =>
    downloadAuditFile({
      path: `/audit/session/${session.id}/report.pdf`,
      mime: "application/pdf",
      defaultName: `audit-${session.id}.pdf`,
      setBusyFlag: setGeneratingPdf,
      label: "PDF report",
    });

  const generateExcel = () =>
    downloadAuditFile({
      path: `/audit/session/${session.id}/report.xlsx`,
      mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      defaultName: `audit-${session.id}.xlsx`,
      setBusyFlag: setGeneratingXlsx,
      label: "Excel working copy",
    });

  const buckets = result?.by_source || {};
  const bucketEntries = Object.entries(buckets);
  const totalBookings = result?.prorated_bookings_count || 0;
  const totalMisc = manualAdjustments.reduce((sum, adj) => sum + (parseFloat(adj.amount) || 0), 0);
  const totalGross = (result?.total_gross || 0) + totalMisc;
  const totalCommission = result?.total_commission || 0;
  const totalRevenue = (result?.total_revenue || 0) + totalMisc;
  const unresolvedIssues = result?.unresolved_issues || [];
  const unaccountedRevenue = result?.unaccounted_revenue || [];

  const effPct = (b) => {
    if (!b) return 0;
    if (b.effective_commission_pct != null) return b.effective_commission_pct;
    if (b.fee_pct != null) return b.fee_pct;
    if (b.gross) return (b.commission / b.gross) * 100;
    return 0;
  };

  const overrideUnaccounted = async (bid) => {
    try {
      await axios.post(`${API}/audit/session/${session.id}/override`, {
        phase: "phase3",
        booking_id: bid,
        override_type: "Ignored Missing Booking"
      });
      // Need to re-run phase 5 so it re-fetches the phase 3 result with the new override
      toast.success("Hidden from Unaccounted Revenue");
      run();
    } catch (e) {
      toast.error(`Failed to ignore booking: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <Button
          data-testid="run-phase5-btn"
          onClick={run}
          disabled={busy || (phase5Ok && locked)}
          className="bg-teal-700 hover:bg-teal-800 text-white"
          title={
            phase5Ok && locked
              ? "Phase 5 is locked. Unlock to re-run."
              : ""
          }
        >
          {busy ? "Running..." : phase5Ok ? "↻ Re-run Phase 5" : "Run Phase 5"}
        </Button>
        <PhaseLockControl
          session={session}
          phaseKey="phase5"
          complete={phase5Ok}
          refresh={refresh}
        />
      </div>

      {phase5Ok && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat
              label="Gross Revenue"
              value={`₹${totalGross.toLocaleString()}`}
            />
            <Stat
              label="Total Commission Paid"
              value={`₹${totalCommission.toLocaleString()}`}
            />
            <Stat
              label="Net Revenue"
              value={`₹${totalRevenue.toLocaleString()}`}
            />
            <Stat label="Total Active Bookings" value={totalBookings} />
          </div>

          <Section title="Net Revenue by Source (Prorated)">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Bookings</TableHead>
                  <TableHead className="text-right">Gross Billed</TableHead>
                  <TableHead className="text-right">OTA Comm</TableHead>
                  <TableHead className="text-right">Stayflexi Comm</TableHead>
                  <TableHead className="text-right">PG (for direct payments)</TableHead>
                  <TableHead className="text-right">Total Comm</TableHead>
                  <TableHead className="text-right">Net Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bucketEntries.map(([k, v]) => (
                  <TableRow key={k} data-testid={`bucket-${k}`}>
                    <TableCell className="text-xs">{k}</TableCell>
                    <TableCell className="text-right text-xs">{v?.bookings_count || 0}</TableCell>
                    <TableCell className="text-right text-xs">
                      ₹{(v?.gross || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-xs whitespace-nowrap">
                      {v?.ota_commission_pct}%<br/><span className="text-muted-foreground">₹{(v?.ota_commission_breakdown || 0).toLocaleString()}</span>
                    </TableCell>
                    <TableCell className="text-right text-xs whitespace-nowrap">
                      {v?.sf_commission_pct}%<br/><span className="text-muted-foreground">₹{(v?.sf_commission_breakdown || 0).toLocaleString()}</span>
                    </TableCell>
                    <TableCell className="text-right text-xs whitespace-nowrap">
                      {v?.pg_commission_pct}%<br/><span className="text-muted-foreground">₹{(v?.pg_commission_breakdown || 0).toLocaleString()}</span>
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      ₹{(v?.commission || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-xs font-semibold">
                      ₹{(v?.revenue || 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {manualAdjustments.map((adj, idx) => {
                  const amt = parseFloat(adj.amount) || 0;
                  if (amt === 0) return null;
                  return (
                    <TableRow key={`adj-${idx}`} className="bg-muted/10">
                      <TableCell className="text-xs">{adj.comments || "Manual Adjustment"}</TableCell>
                      <TableCell className="text-right text-xs">—</TableCell>
                      <TableCell className="text-right text-xs">₹{amt.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-xs">—</TableCell>
                      <TableCell className="text-right text-xs">—</TableCell>
                      <TableCell className="text-right text-xs">—</TableCell>
                      <TableCell className="text-right text-xs">—</TableCell>
                      <TableCell className="text-right text-xs font-semibold">₹{amt.toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow data-testid="bucket-total">
                  <TableCell className="text-xs font-semibold">Total</TableCell>
                  <TableCell className="text-right text-xs font-semibold">
                    {totalBookings}
                  </TableCell>
                  <TableCell className="text-right text-xs font-semibold">
                    ₹{totalGross.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-xs font-semibold">—</TableCell>
                  <TableCell className="text-right text-xs font-semibold">—</TableCell>
                  <TableCell className="text-right text-xs font-semibold">—</TableCell>
                  <TableCell className="text-right text-xs font-semibold">
                    ₹{totalCommission.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-xs font-semibold">
                    ₹{totalRevenue.toLocaleString()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Section>

          <Section title="Booking Share & Commission % per Source">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Booking Share</TableHead>
                  <TableHead className="text-right">Commission %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bucketEntries.map(([k, v]) => {
                  const share = totalBookings
                    ? ((v.bookings_count || 0) / totalBookings) * 100
                    : 0;
                  return (
                    <TableRow key={k} data-testid={`share-${k}`}>
                      <TableCell className="text-xs">{k}</TableCell>
                      <TableCell className="text-right text-xs">
                        {share.toFixed(1)}% ({v.bookings_count || 0})
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <Badge variant="outline" className={Number(effPct(v)) > 25 ? "bg-red-50 text-red-700" : ""}>
                          {isNaN(Number(effPct(v))) ? "N/A" : `${Number(effPct(v)).toFixed(2)}%`}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Section>

          <Section title="Expected Inflow by Payment Mode">
            {result?.payment_channels ? (
              <div className="space-y-4">
                {Object.entries({
                  "OTA Payout (Bank Transfer)": result.payment_channels.ota_account,
                  "Razorpay (Gateway)": result.payment_channels.gateway,
                  "Property Desk (UPI/Cash/POS)": result.payment_channels.property_desk
                }).map(([sectionTitle, sectionData]) => {
                  const items = Object.entries(sectionData || {});
                  if (items.length === 0) return null;
                  const sectionTotalGross = items.reduce((acc, [_, v]) => acc + v.gross, 0);
                  const sectionTotalComm = items.reduce((acc, [_, v]) => acc + v.comm, 0);
                  const sectionTotalNet = items.reduce((acc, [_, v]) => acc + v.net, 0);
                  return (
                    <div key={sectionTitle}>
                      <h4 className="text-xs font-semibold text-slate-700 mb-2 px-1">{sectionTitle}</h4>
                      <Table className="mb-2 border-b border-slate-100 pb-0">
                        <TableHeader>
                          <TableRow className="bg-slate-50/50">
                            <TableHead className="py-1 h-7">Source</TableHead>
                            <TableHead className="text-right py-1 h-7">Gross</TableHead>
                            <TableHead className="text-right py-1 h-7">Commission</TableHead>
                            <TableHead className="text-right py-1 h-7">Net Expected</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map(([source, vals]) => (
                            <TableRow key={source} className="border-slate-50">
                              <TableCell className="text-xs font-medium py-1">{source}</TableCell>
                              <TableCell className="text-right text-xs py-1">₹{vals.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                              <TableCell className="text-right text-xs text-slate-500 py-1">₹{vals.comm.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                              <TableCell className="text-right text-xs font-medium py-1">₹{vals.net.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-slate-50/50">
                            <TableCell className="text-xs font-bold py-1">Section Total</TableCell>
                            <TableCell className="text-right text-xs font-bold py-1">₹{sectionTotalGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right text-xs font-bold py-1">₹{sectionTotalComm.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right text-xs font-bold py-1">₹{sectionTotalNet.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  );
                })}
                <div className="flex flex-col gap-1 items-end mt-4 pt-4 border-t border-slate-200">
                  {totalMisc !== 0 && (
                    <div className="flex justify-between w-full sm:w-1/2 text-slate-500">
                      <span className="text-xs italic">Manual Adjustments (Applied Later)</span>
                      <span className="text-right text-xs italic">₹{totalMisc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between w-full sm:w-1/2">
                    <span className="text-sm font-bold text-slate-900">Total Net Revenue</span>
                    <span className="text-right text-sm font-bold text-slate-900">₹{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  {result?.expected_gst != null && (
                    <div className="flex justify-between w-full sm:w-1/2 text-slate-500 mt-2">
                      <span className="text-xs">Estimated GST Liability (inclusive)</span>
                      <span className="text-right text-xs">₹{result.expected_gst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500 italic p-4 text-center">Payment channels unavailable for this session.</p>
            )}
          </Section>

          {unresolvedIssues.length > 0 && (
            <Section title="Total Issues / Discrepancies">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-500 italic">
                  These bookings are excluded from the main verified summary above.
                </p>
                <Badge variant="outline" className="bg-red-50 text-red-700 font-semibold border-red-200">
                  Total Value at Risk: ₹{unresolvedIssues.reduce((sum, u) => sum + (u.amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Badge>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking ID</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Issue Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unresolvedIssues.map((u, i) => (
                    <TableRow key={i} className="bg-red-50/30 hover:bg-red-50/50">
                      <TableCell className="text-xs font-medium">{u.booking_id}</TableCell>
                      <TableCell className="text-xs">{u.source}</TableCell>
                      <TableCell className="text-right text-xs text-red-600 font-semibold">
                        ₹{(u.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-xs max-w-sm truncate text-slate-700" title={u.issue}>
                        {u.issue}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {unaccountedRevenue.length > 0 && (
            <Section title="Unaccounted Revenue (Missing in Stayflexi)">
              <p className="text-xs text-slate-500 mb-2 italic">
                Payment was received for these bookings, but they do not exist in Stayflexi. This is informational and does not affect the verified revenue totals above.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking ID</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Issue Description</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unaccountedRevenue.map((u, i) => (
                    <TableRow key={i} className="bg-green-50/30 hover:bg-green-50/50">
                      <TableCell className="text-xs font-medium">{u.booking_id}</TableCell>
                      <TableCell className="text-xs">{u.source}</TableCell>
                      <TableCell className="text-right text-xs text-green-700 font-semibold">
                        ₹{(u.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-xs max-w-sm truncate text-slate-700" title={u.issue}>
                        {u.issue}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-slate-500 hover:text-slate-900"
                          onClick={() => overrideUnaccounted(u.booking_id)}
                          disabled={locked || busy}
                        >
                          Ignore
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {/* Manual Adjustments */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Manual Adjustments</CardTitle>
              <CardDescription className="text-xs">
                Add miscellaneous revenue or manual adjustments not captured by the reports (e.g. cash collected for OTA no-shows).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {manualAdjustments.map((adj, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Amount (₹)</Label>
                      <Input 
                        type="number" 
                        value={adj.amount} 
                        onChange={e => updateAdjustment(idx, "amount", e.target.value)} 
                        onBlur={() => handleMiscSave()}
                        disabled={locked || busy}
                        className="w-32 text-right"
                        placeholder="e.g. -500 or 1000"
                      />
                    </div>
                    <div className="space-y-1 flex-1 w-full">
                      <Label className="text-xs text-slate-500">Comments / Notes</Label>
                      <Input
                        type="text"
                        value={adj.comments}
                        onChange={e => updateAdjustment(idx, "comments", e.target.value)}
                        onBlur={() => handleMiscSave()}
                        disabled={locked || busy}
                        placeholder="E.g., Booking 12563 OTA forgave commission, collected cash directly"
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => removeAdjustment(idx)} 
                      disabled={locked || busy}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 sm:mt-5"
                      title="Remove adjustment"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {manualAdjustments.length < 3 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={addAdjustment} 
                    disabled={locked || busy}
                    className="mt-2 text-xs h-8"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Adjustment
                  </Button>
                )}
                {manualAdjustments.length === 0 && (
                  <p className="text-xs text-slate-500 italic">No manual adjustments added.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Verify + generate report */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Final sign-off & Report</CardTitle>
              <CardDescription className="text-xs">
                Mark this audit as verified to enable the downloadable report.
                Re-running Phase 4 will reset verification.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="verified-switch"
                  checked={verified}
                  onCheckedChange={toggleVerified}
                />
                <Label className="text-sm">
                  {verified ? "Verified ✅" : "Mark as verified"}
                </Label>
              </div>
              <Button
                data-testid="generate-report-btn"
                onClick={generateReport}
                disabled={!verified || generatingPdf}
              >
                {generatingPdf ? "Generating…" : "Generate & Download Report (PDF)"}
              </Button>
              <Button
                data-testid="generate-excel-btn"
                onClick={generateExcel}
                disabled={generatingXlsx}
                variant="outline"
                title="Auditor's full working copy — every bucket, every flagged row, multi-sheet workbook. Always available."
              >
                {generatingXlsx ? "Generating…" : "Download Excel (working copy)"}
              </Button>
              {!verified && (
                <span className="text-xs text-muted-foreground">
                  Toggle verified to enable PDF report. Excel is always available.
                </span>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {result && !result.ok && (
        <p className="text-destructive text-sm">{result.error}</p>
      )}
    </div>
  );
}


export default Phase5;
